import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/api";
import { Loading } from "@/components/common";

export function AuthCallback() {
  const { session, loading, me } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && session) nav(me?.onboarding_complete ? "/dashboard" : "/onboarding", { replace: true });
  }, [loading, session, me, nav]);
  if (loading || session) return <Loading label="Completing sign-in" />;
  return <main className="grid min-h-screen place-items-center px-5"><section className="panel w-full max-w-md space-y-4 p-6"><h1 className="text-2xl font-bold">This email link couldn't be verified</h1><p className="text-sm text-muted-foreground">It may have expired or already been used. Sign in if your email is verified, or return to signup to request a fresh verification link.</p><div className="flex flex-wrap gap-3"><Button asChild><a href="/login">Sign in</a></Button><Button asChild variant="outline"><a href="/signup">Return to signup</a></Button></div></section></main>;
}

export function ResetPassword() {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Password updated");
    nav("/jobs");
  };
  return (
    <div className="grid min-h-screen place-items-center px-5">
      <form onSubmit={submit} className="panel w-full max-w-sm space-y-4 p-6" data-testid="reset-password-form">
        <h1 className="text-2xl font-bold">Set a new password</h1>
        <div className="space-y-1.5"><Label htmlFor="newpw">New password</Label>
          <Input id="newpw" type="password" minLength={12} autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          <p className="text-xs text-muted-foreground">Use at least 12 characters and a unique password.</p></div>
        <Button className="w-full" disabled={busy} data-testid="reset-password-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update password</Button>
      </form>
    </div>
  );
}

export function Mfa() {
  const { reload, aal } = useAuth();
  const nav = useNavigate();
  const [factor, setFactor] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const verified = data?.totp?.find((f) => f.status === "verified");
      if (verified) setFactor(verified);
    });
  }, []);

  const startEnroll = async () => {
    const { data: existing } = await supabase.auth.mfa.listFactors();
    for (const f of existing?.all || []) if (f.status !== "verified") await supabase.auth.mfa.unenroll({ factorId: f.id });
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: `AJOBF admin ${Date.now()}` });
    if (error) return toast.error(error.message);
    setEnroll(data);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    const factorId = factor?.id || enroll?.id;
    const { data: ch, error: ce } = await supabase.auth.mfa.challenge({ factorId });
    if (ce) { setBusy(false); return toast.error(ce.message); }
    const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
    setBusy(false);
    if (error) return toast.error("Invalid code. Try the current 6-digit code.");
    await supabase.auth.refreshSession();
    await reload();
    toast.success("MFA verified");
    nav("/admin");
  };

  useEffect(() => {
    if (aal.current === "aal2") nav("/admin", { replace: true });
  }, [aal, nav]);
  return (
    <div className="mx-auto max-w-md space-y-6 fade-up" data-testid="mfa-page">
      <div>
        <p className="eyebrow mb-2 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Super Admin</p>
        <h1 className="text-3xl font-bold">Authenticator verification</h1>
        <p className="mt-2 text-sm text-muted-foreground">The admin console requires a second factor from an authenticator app (TOTP). Email codes are not accepted.</p>
      </div>
      {!factor && !enroll && (
        <Button onClick={startEnroll} data-testid="mfa-enroll-button"><KeyRound className="mr-2 h-4 w-4" /> Set up authenticator app</Button>
      )}
      {enroll && (
        <div className="panel space-y-3 p-5" data-testid="mfa-enroll-panel">
          <p className="text-sm">Scan this QR code with Google Authenticator, 1Password, Authy or similar.</p>
          <img src={enroll.totp.qr_code} alt="Authenticator QR code" className="h-44 w-44 rounded-lg bg-white p-2" />
          <p className="break-all font-mono text-xs text-muted-foreground">Manual key: {enroll.totp.secret}</p>
        </div>
      )}
      {(factor || enroll) && (
        <form onSubmit={verify} className="panel space-y-3 p-5">
          <Label htmlFor="totp">6-digit code</Label>
          <Input id="totp" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-code-input" />
          <Button className="w-full" disabled={busy} data-testid="mfa-verify-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify</Button>
        </form>
      )}
    </div>
  );
}
